import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardMedia,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Chip,
  Pagination,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ImageIcon from '@mui/icons-material/Image';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import AddIcon from '@mui/icons-material/Add';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

import { uploadService, MediaItem } from '../../services/api/uploadService';
import { useAuth } from '../../contexts/AuthContext';
import MediaUpload from './MediaUpload';

interface MediaSelectorProps {
  mediaType: 'image' | 'video' | 'both';
  onSelect: (url: string, altText?: string) => void;
  initialMediaUrl?: string;
  initialAltText?: string;
  label?: string;
}

const MediaSelector: React.FC<MediaSelectorProps> = ({
  mediaType,
  onSelect,
  initialMediaUrl = '',
  initialAltText = '',
  label = 'Select Media'
}) => {
  const { tokens } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<MediaItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentMediaType, setCurrentMediaType] = useState<'all' | 'image' | 'video'>(
    mediaType === 'image' ? 'image' : 
    mediaType === 'video' ? 'video' : 'all'
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  
  // State for the selected media
  const [selectedMedia, setSelectedMedia] = useState<string>(initialMediaUrl || '');
  const [selectedAltText, setSelectedAltText] = useState<string>(initialAltText || '');
  
  // State for upload dialog
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  
  // Load media items when dialog opens
  useEffect(() => {
    if (open) {
      loadMediaItems();
    }
  }, [open]);
  
  // Filter media items based on search and type
  useEffect(() => {
    if (mediaItems.length === 0) return;
    
    let filtered = [...mediaItems];
    
    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
        item.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.altText?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Filter by media type
    if (currentMediaType !== 'all') {
      filtered = filtered.filter(item => item.type === currentMediaType);
    } else if (mediaType !== 'both') {
      // If component is restricted to a specific type, always filter
      filtered = filtered.filter(item => 
        mediaType === 'image' ? item.type === 'image' : 
        mediaType === 'video' ? item.type === 'video' : true
      );
    }
    
    setFilteredItems(filtered);
    setCurrentPage(1);
  }, [mediaItems, searchTerm, currentMediaType, mediaType]);
  
  const loadMediaItems = async () => {
    if (!tokens?.idToken) return;
    
    try {
      setLoading(true);
      const items = await uploadService.getAllMedia(tokens.idToken);
      setMediaItems(items);
      setFilteredItems(items);
    } catch (error) {
      console.error('Error loading media:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpen = () => {
    setOpen(true);
  };
  
  const handleClose = () => {
    setOpen(false);
  };
  
  const handleSelect = (item: MediaItem) => {
    setSelectedMedia(item.url);
    setSelectedAltText(item.altText || '');
    onSelect(item.url, item.altText);
    handleClose();
  };
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  const handleMediaTypeChange = (event: React.MouseEvent<HTMLElement>, newType: 'all' | 'image' | 'video' | null) => {
    if (newType !== null) {
      setCurrentMediaType(newType);
    }
  };
  
  const handleViewModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'grid' | 'list' | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };
  
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };
  
  const handleOpenUploadDialog = () => {
    setShowUploadDialog(true);
  };
  
  const handleCloseUploadDialog = () => {
    setShowUploadDialog(false);
  };
  
  const handleUploadSuccess = (url: string, altText?: string) => {
    // Close the upload dialog
    setShowUploadDialog(false);
    
    // Set the selected media
    setSelectedMedia(url);
    setSelectedAltText(altText || '');
    onSelect(url, altText);
    
    // Refresh the media list
    loadMediaItems();
  };
  
  // Pagination calculation
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
  const pageCount = Math.ceil(filteredItems.length / itemsPerPage);
  
  // Render media preview
  const renderMediaPreview = (item: MediaItem) => {
    if (item.type === 'image') {
      return (
        <CardMedia
          component="img"
          height={viewMode === 'grid' ? 140 : 100}
          image={item.url}
          alt={item.altText || item.name}
          sx={{ 
            objectFit: 'cover',
            transition: 'transform 0.3s ease',
            '&:hover': { transform: 'scale(1.05)' }
          }}
        />
      );
    } else { // video
      return (
        <Box position="relative" height={viewMode === 'grid' ? 140 : 100}>
          <CardMedia
            component="video"
            height={viewMode === 'grid' ? 140 : 100}
            src={item.url}
            muted
            preload="metadata"
            sx={{
              objectFit: 'cover',
              width: '100%',
              bgcolor: 'black',
            }}
            onMouseOver={(e) => {
              const video = e.target as HTMLVideoElement;
              if (video.paused) video.play();
            }}
            onMouseOut={(e) => {
              const video = e.target as HTMLVideoElement;
              if (!video.paused) {
                video.pause();
                video.currentTime = 0;
              }
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.3)',
              opacity: 0.7,
              transition: 'opacity 0.3s',
              '&:hover': {
                opacity: 0.3,
              }
            }}
          >
            <PlayCircleOutlineIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
        </Box>
      );
    }
  };
  
  // Function to check if current selection is a video
  const isSelectedMediaVideo = () => {
    return selectedMedia && (
      selectedMedia.includes('.mp4') || 
      selectedMedia.includes('.webm') || 
      selectedMedia.includes('.avi') ||
      selectedMedia.includes('youtube.com') || 
      selectedMedia.includes('youtu.be') ||
      selectedMedia.includes('vimeo.com')
    );
  };
  
  // Current selected preview
  const renderSelectedPreview = () => {
    if (!selectedMedia) return null;
    
    if (isSelectedMediaVideo()) {
      return (
        <Box position="relative" height={140} width="100%">
          <Box
            component="video"
            src={selectedMedia}
            sx={{ 
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              bgcolor: 'black',
            }}
            controls
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              px: 1,
              py: 0.5,
              borderRadius: 1,
            }}
          >
            Video
          </Typography>
        </Box>
      );
    } else {
      return (
        <Box
          component="img"
          src={selectedMedia}
          alt={selectedAltText}
          sx={{ 
            maxHeight: 140,
            maxWidth: '100%',
            objectFit: 'contain',
          }}
        />
      );
    }
  };
  
  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        {label}
      </Typography>
      
      {selectedMedia ? (
        <Box
          sx={{
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            overflow: 'hidden',
            mb: 2,
            position: 'relative',
          }}
        >
          <Box display="flex" justifyContent="center" alignItems="center" p={2}>
            {renderSelectedPreview()}
          </Box>
          <Box display="flex" justifyContent="space-between" p={1} bgcolor="#f5f5f5">
            <Typography variant="caption" noWrap sx={{ flex: 1, mr: 1 }}>
              {selectedMedia.split('/').pop()}
            </Typography>
            <Button size="small" onClick={handleOpen}>
              Change
            </Button>
          </Box>
        </Box>
      ) : (
        <Button 
          variant="outlined" 
          startIcon={mediaType === 'image' ? <ImageIcon /> : mediaType === 'video' ? <VideoLibraryIcon /> : <InsertDriveFileIcon />}
          onClick={handleOpen}
          fullWidth
          sx={{ mb: 2, height: 100, border: '1px dashed #ccc' }}
        >
          Select from Media Library
        </Button>
      )}
      
      {/* Media selection dialog */}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Select Media</Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {/* Search and filter controls */}
          <Box mb={3}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  placeholder="Search media..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} md={7}>
                <Box display="flex" justifyContent="space-between">
                  <Box>
                    {/* Only show media type filter if both types are allowed */}
                    {mediaType === 'both' && (
                      <ToggleButtonGroup
                        value={currentMediaType}
                        exclusive
                        onChange={handleMediaTypeChange}
                        size="small"
                      >
                        <ToggleButton value="all">
                          <Tooltip title="All Media">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <InsertDriveFileIcon fontSize="small" sx={{ mr: 0.5 }} />
                              <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>All</Box>
                            </Box>
                          </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="image">
                          <Tooltip title="Images Only">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <ImageIcon fontSize="small" sx={{ mr: 0.5 }} />
                              <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>Images</Box>
                            </Box>
                          </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="video">
                          <Tooltip title="Videos Only">
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <VideoLibraryIcon fontSize="small" sx={{ mr: 0.5 }} />
                              <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>Videos</Box>
                            </Box>
                          </Tooltip>
                        </ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  </Box>
                  
                  <Box display="flex" alignItems="center">
                    {/* View mode toggle */}
                    <ToggleButtonGroup
                      value={viewMode}
                      exclusive
                      onChange={handleViewModeChange}
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      <ToggleButton value="grid">
                        <GridViewIcon fontSize="small" />
                      </ToggleButton>
                      <ToggleButton value="list">
                        <ViewListIcon fontSize="small" />
                      </ToggleButton>
                    </ToggleButtonGroup>
                    
                    {/* Upload new button */}
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleOpenUploadDialog}
                      size="small"
                    >
                      Upload New
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
          
          {/* Results count */}
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">
              {filteredItems.length} items found
            </Typography>
          </Box>
          
          {/* Media grid */}
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
              <CircularProgress />
            </Box>
          ) : filteredItems.length === 0 ? (
            <Box textAlign="center" py={5}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No media items found
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleOpenUploadDialog}
                sx={{ mt: 2 }}
              >
                Upload New Media
              </Button>
            </Box>
          ) : viewMode === 'grid' ? (
            <Grid container spacing={2}>
              {currentItems.map((item) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3
                      }
                    }}
                    onClick={() => handleSelect(item)}
                  >
                    <Box sx={{ position: 'relative' }}>
                      {renderMediaPreview(item)}
                    </Box>
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" noWrap>
                        {item.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.uploadDate.toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box>
              {currentItems.map((item) => (
                <Paper
                  key={item.id}
                  sx={{
                    p: 1,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                  }}
                  onClick={() => handleSelect(item)}
                >
                  <Box sx={{ width: 100, flexShrink: 0, mr: 2 }}>
                    {renderMediaPreview(item)}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2">{item.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.uploadDate.toLocaleDateString()} • {item.altText || 'No description'}
                    </Typography>
                    {item.tags && item.tags.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                        {item.tags.map((tag) => (
                          <Chip key={tag} label={tag} size="small" />
                        ))}
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
          
          {/* Pagination */}
          {pageCount > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={pageCount}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Upload dialog */}
      <Dialog open={showUploadDialog} onClose={handleCloseUploadDialog} maxWidth="md">
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Upload New Media</Typography>
            <IconButton onClick={handleCloseUploadDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <MediaUpload
            mediaType={mediaType}
            onUploadSuccess={handleUploadSuccess}
            label={`Upload ${mediaType === 'image' ? 'Image' : mediaType === 'video' ? 'Video' : 'Media'}`}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MediaSelector; 